import { X } from 'lucide-react'
import { useState } from 'react'
import { needsApi } from '../api/endpoints'
import { ApiError } from '../api/client'
import { ChipGroup } from './ChipGroup'
import { hasTutorArrangement, OFFER_ONLY_DETAIL_KEYS } from '../utils/institutionOffer'
import { ErrorMessage } from './ErrorMessage'
import { useTaxonomy } from '../taxonomy/TaxonomyContext'
import { readDetailField, toStringArray } from '../utils/taxonomy'
import type { LearnerNeedResponse, NeedCategory } from '../types/api'

/** Reads every detail answer off an existing need, keyed the way the taxonomy declares them. */
function initialDetail(detail: unknown, keys: string[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const k of keys) out[k] = readDetailField(detail, k)
  return out
}

export function NeedForm({
  initial,
  onCancel,
  onSaved,
}: {
  initial?: LearnerNeedResponse
  onCancel: () => void
  onSaved: () => void
}) {
  // Every option below comes from GET /api/taxonomy. The server rejects anything outside those
  // lists rather than coercing it, so a locally hard-coded list would offer the user choices that
  // cannot be saved - which is exactly what it used to do.
  const { categories, labelOf, itemsOf, levelsOf, categoryOf, common, loading, error: taxonomyError } = useTaxonomy()

  const [category, setCategory] = useState<NeedCategory>(initial?.category ?? 'ACADEMICS')
  const [detail, setDetail] = useState<Record<string, string>>(() =>
    initialDetail(initial?.detail, Object.keys(categoryOf(initial?.category ?? 'ACADEMICS')?.detail ?? {})),
  )
  const [subjects, setSubjects] = useState<string[]>(toStringArray(initial?.items))
  const [level, setLevel] = useState(initial?.level ?? '')
  const [mode, setMode] = useState(initial?.mode ?? '')
  const [timing, setTiming] = useState<string[]>(toStringArray(initial?.timing))
  const [budgetBand, setBudgetBand] = useState(initial?.budgetBand ?? '')
  const [preferredTutorGender, setPreferredTutorGender] = useState(initial?.preferredTutorGender ?? '')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const activeCategory = categoryOf(category)
  const noTutorArrangement = !hasTutorArrangement(category)
  // The category's detail list carries both sides' questions. A learner is asked their half:
  // nobody looking for a school states its tuition fee.
  const offerOnly = OFFER_ONLY_DETAIL_KEYS[category] ?? []
  const detailQuestions = Object.entries(activeCategory?.detail ?? {}).filter(
    ([key]) => !offerOnly.includes(key),
  )
  // null levels means the category is graded by its own detail questions (class, target year)
  // rather than by skill level - see the taxonomy contract.
  const levels = levelsOf(category)

  function toggleSubject(subject: string) {
    setSubjects((prev) => (prev.includes(subject) ? prev.filter((s) => s !== subject) : [...prev, subject]))
  }

  function toggleTiming(slot: string) {
    setTiming((prev) => (prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot]))
  }

  function selectCategory(c: NeedCategory) {
    setCategory(c)
    // Items, level and detail answers are all category-scoped; carrying them across would submit
    // values the new category does not allow.
    setSubjects([])
    setLevel('')
    setDetail({})
  }

  async function onSubmit() {
    setError(null)
    if (subjects.length === 0) {
      setError(
        `Choose at least one option under "${activeCategory?.itemsLabel ?? 'Subjects'}" - `
          + 'tutors are matched to you by category and subject, so this is required.',
      )
      return
    }
    setSubmitting(true)
    try {
      const detailPayload = Object.fromEntries(
        Object.entries(detail).filter(([, v]) => v),
      )
      const body = {
        category,
        items: subjects,
        detail: Object.keys(detailPayload).length ? detailPayload : undefined,
        level: levels && level ? level : undefined,
        // Cleared rather than omitted for a category with no tutor arrangement, so a need switched
        // over to one does not keep a tutor preference nobody can see or act on.
        mode: noTutorArrangement ? undefined : mode || undefined,
        timing: noTutorArrangement ? [] : timing,
        budgetBand: noTutorArrangement ? undefined : budgetBand || undefined,
        preferredTutorGender: noTutorArrangement ? undefined : preferredTutorGender || undefined,
        isPrimary: initial?.isPrimary ?? false,
      }
      if (initial) {
        await needsApi.update(initial.id, body)
      } else {
        await needsApi.create(body)
      }
      onSaved()
    } catch (err) {
      // The server names every offending value; showing only "Bad Request" would hide which
      // chip it objected to.
      if (err instanceof ApiError) {
        setError(err.details?.length ? `${err.message}: ${err.details.join(', ')}` : err.message)
      } else {
        setError('Failed to save')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <div className="card-pad text-sm text-ink-500">Loading options...</div>
  }
  if (taxonomyError) {
    return (
      <div className="card-pad space-y-3">
        <ErrorMessage message={`Could not load the options list: ${taxonomyError}`} />
        <button type="button" onClick={onCancel} className="btn-ghost">Close</button>
      </div>
    )
  }

  return (
    <div className="card-pad space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-ink-900">{initial ? 'Edit a need' : 'Add a need'}</h2>
        <button type="button" onClick={onCancel} className="grid h-8 w-8 place-items-center rounded-full border border-ink-200 hover:bg-ink-100">
          <X size={14} />
        </button>
      </div>

      <p className="rounded-xl border border-sun-400/50 bg-sun-400/10 px-3 py-2 text-xs text-amber-900">
        Category and {activeCategory?.itemsLabel ?? 'Subjects'} are both required - the map only
        shows you to tutors who teach exactly what you pick here.
      </p>

      <div className="category-panel">
        <span className="label label-required">Category</span>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c.name}
              type="button"
              onClick={() => selectCategory(c.name)}
              className={`chip ${category === c.name ? 'chip-active-category' : ''}`}
            >
              {labelOf(c.name)}
            </button>
          ))}
        </div>
      </div>

      {/* Everything below is category-scoped and changes out from under you when you pick a
          different category above - the line makes that boundary visible instead of letting the
          whole form look like it just silently changed shape. */}
      <div className="border-t border-ink-200" />

      {/* Each category declares its own questions - class/board for Academics, target year for
          Competitive Exams, goal for Sports - so they are rendered from the taxonomy rather than
          special-cased here. */}
      {detailQuestions.map(([key, options]) => (
        <ChipGroup
          key={key}
          label={key.replace(/_/g, ' ').replace(/^./, (ch) => ch.toUpperCase())}
          options={options}
          value={detail[key] ?? ''}
          onChange={(v) => setDetail((prev) => ({ ...prev, [key]: v }))}
        />
      ))}

      {levels ? <ChipGroup label="Level" options={levels} value={level} onChange={setLevel} /> : null}

      <ChipGroup
        label={activeCategory?.itemsLabel ?? 'Subjects'}
        required
        options={itemsOf(category)}
        values={subjects}
        onToggle={toggleSubject}
      />
      {/* All four describe an arrangement with a tutor - how they teach, when, at what hourly
          price, and whether a man or a woman. None of them means anything when the other side is
          a school admitting a class, a college running a programme, a company offering an
          internship, or an entrepreneur's match (a co-founder, mentor or investor) - see
          hasTutorArrangement. */}
      {hasTutorArrangement(category) && (
        <>
          <ChipGroup label="Preferred mode" options={common.mode} value={mode} onChange={setMode} />
          <ChipGroup label="Class timing" options={common.timing} values={timing} onToggle={toggleTiming} />
          <ChipGroup label="Budget" options={common.budget_band} value={budgetBand} onChange={setBudgetBand} />
          <ChipGroup
            label="Preferred tutor"
            options={common.preferred_tutor_gender}
            value={preferredTutorGender}
            onChange={setPreferredTutorGender}
          />
        </>
      )}

      <ErrorMessage message={error} />

      <div className="flex gap-2">
        <button type="button" onClick={onSubmit} disabled={submitting} className="btn-sun">
          {submitting ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
      </div>
    </div>
  )
}
